import { useAppDispatch } from "hooks/storeHooks"
import { ChangeEvent, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { getTintVTOService } from "services/tintvto"
import { LanguageService } from "services/languageService"
import { setTypes } from "store/product/productSlice"
import { setUserInfo } from "store/user/userSlice"
import { IntegrationType, UserStatusTypes } from "types"
import { Box, Button, Checkbox, Layout, Link, TextInput, Typography } from "@strapi/design-system"
import { useQueryClient } from "@tanstack/react-query"
import * as Sentry from "@sentry/react"
import { isDevelopment } from "../../config/typedEnv"

const STORAGE_KEY = "lastLogin"
const STORAGE_PASSWORD_KEY = "lastPassword"
const STORAGE_REMEMBER_KEY = "rememberMe"

const setUserInSentry = (user: { id: string; email?: string; username?: string }) => {
  if (!isDevelopment)
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    })
}
export const LoginPage = () => {
  const { t, i18n } = useTranslation()
  const service = getTintVTOService()
  const dispatch = useAppDispatch()
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [login, setLogin] = useState(localStorage.getItem(STORAGE_KEY) || "")
  const [password, setPassword] = useState(
    localStorage.getItem(STORAGE_REMEMBER_KEY) === "true"
      ? localStorage.getItem(STORAGE_PASSWORD_KEY) || ""
      : ""
  )
  const [rememberMe, setRememberMe] = useState(
    localStorage.getItem(STORAGE_REMEMBER_KEY) === "true"
  )
  const [touched, setTouched] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (login) setTouched(true)
    queryClient.clear()
  }, [login, queryClient])

  const onLogin = useCallback(async () => {
    setTouched(true)
    if (!login || !password) return

    setLoading(true)
    const response = await service.login(login, password)

    if (!response) {
      setError(t("loginPage.invalidCredentials"))
    } else {
      localStorage.setItem(STORAGE_KEY, login)

      if (rememberMe) {
        localStorage.setItem(STORAGE_PASSWORD_KEY, password)
        localStorage.setItem(STORAGE_REMEMBER_KEY, "true")
      } else {
        localStorage.removeItem(STORAGE_PASSWORD_KEY)
        localStorage.setItem(STORAGE_REMEMBER_KEY, "false")
      }
      const user = {
        userInfo: {
          username: response.data.username,
          status: response.data.status ?? UserStatusTypes.NEW,
          merchantId: response.data.merchantId,
          groups: response.data.groups,
          integrationType: IntegrationType.COMMON,
          setUserExpired: false,
          ...response.data.login,
        },
      }

      setUserInSentry({
        id:
          user.userInfo.username +
          "(" +
          user.userInfo.integrationType +
          ")" +
          "( merchantId:" +
          user.userInfo.merchantId +
          ")",
      })

      dispatch(setUserInfo(user))
      service.changeAwsTokenAndUserType(null, user.userInfo.integrationType)

      const { data } = await service.types()
      dispatch(setTypes(data?.types ?? []))

      // Set language based on user choice or default AFTER types are loaded
      if (!LanguageService.hasUserMadeChoice()) {
        const defaultLanguage = LanguageService.getDefaultLanguage(user.userInfo.integrationType)
        LanguageService.saveLanguage(defaultLanguage)
        i18n.changeLanguage(defaultLanguage)
      }
    }
    setLoading(false)
  }, [login, password, rememberMe, t, dispatch, i18n, service])
  return (
    <Box
      background="neutral200"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        flexDirection: "column",
      }}
    >
      <Layout>
        <Box
          background="neutral100"
          padding={6}
          paddingBottom={2}
          style={{
            width: "30rem",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            borderRadius: "1rem",
          }}
        >
          <Box
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}
          >
            <img src="tint-logo.svg" alt={t("loginPage.logoAlt")} width="64px" height="64px" />
            <Typography variant="alpha" as="h1">
              {t("loginPage.welcomeTitle")}
            </Typography>
          </Box>
          <Box padding={4} />
          <TextInput
            type="text"
            label="Login"
            name="username"
            autoComplete="username"
            value={login}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setLogin(e.target.value)}
            error={!login && touched}
          />
          <Box padding={2} />
          <TextInput
            type="password"
            label="Password"
            name="password"
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            error={!password && touched}
            onKeyPress={(event) => event.key === "Enter" && onLogin()}
          />
          <Box padding={4}>
            <Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}>
              {t("loginPage.rememberMe")}
            </Checkbox>
          </Box>
          <Box padding={2}>{error && <Typography textColor="danger600">{error}</Typography>}</Box>
          <Button variant="default" onClick={onLogin} size="L" loading={loading}>
            {t("loginPage.loginButton")}
          </Button>
          <Box padding={2} paddingTop={4}>
            <Link to="https://www.banuba.com/support" target="_blank">
              {t("loginPage.supportLink")}
            </Link>
          </Box>
        </Box>
      </Layout>
    </Box>
  )
}
